import { useI18n } from "../i18n/useI18n";
import { numberLocaleFor } from "../i18n/locale";
import type { Workload } from "../lib/types";

type Props = {
  subtitle: string;
  /** Додатковий рядок залежно від режиму «простий / технічний». */
  audienceSub?: string;
  baseUsers: number;
  baseRequests: number;
  userMult: number;
  reqMult: number;
  onUserMult: (n: number) => void;
  onReqMult: (n: number) => void;
  effective: Workload;
  totalMonthly: number;
};

export function WhatIfSliders({
  subtitle,
  audienceSub,
  baseUsers,
  baseRequests,
  userMult,
  reqMult,
  onUserMult,
  onReqMult,
  effective,
  totalMonthly,
}: Props) {
  const { locale, t } = useI18n();
  const nf = numberLocaleFor(locale);
  const fmt = (n: number) => n.toLocaleString(nf, { maximumFractionDigits: 0 });

  return (
    <div className="what-if">
      <h2>{t("whatif.title")}</h2>
      <p className="sub">{subtitle}</p>
      {audienceSub ? <p className="sub audience-sub">{audienceSub}</p> : null}

      <div className="sliders-grid">
        <label className="slider-row">
          <span className="slider-label">
            {t("whatif.users")} {fmt(baseUsers)} →{" "}
            <strong>{fmt(effective.usersDaily)}</strong> (×{userMult.toFixed(1)})
          </span>
          <input
            type="range"
            min={0.5}
            max={10}
            step={0.5}
            value={userMult}
            onChange={(e) => onUserMult(Number(e.target.value))}
          />
        </label>

        <label className="slider-row">
          <span className="slider-label">
            {t("whatif.requests")} {fmt(baseRequests)} →{" "}
            <strong>{fmt(effective.requestsPerUser)}</strong> (×{reqMult.toFixed(1)})
          </span>
          <input
            type="range"
            min={0.5}
            max={5}
            step={0.5}
            value={reqMult}
            onChange={(e) => onReqMult(Number(e.target.value))}
          />
        </label>
      </div>

      <div className="live">
        <span>{t("whatif.liveTotal")}</span>
        <strong>
          {totalMonthly.toLocaleString(nf, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          })}
        </strong>
        <span className="muted">{t("breakdown.perMonth")}</span>
      </div>

      <style>{`
        .what-if h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .sub {
          margin: 0 0 0.55rem;
          font-size: 0.8rem;
          color: var(--muted);
          max-width: 48rem;
        }
        .sub.audience-sub {
          margin-top: -0.35rem;
          margin-bottom: 0.65rem;
          font-size: 0.78rem;
          line-height: 1.45;
          opacity: 0.95;
        }
        .sliders-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.1rem 2rem;
          align-items: start;
          margin-bottom: 0.25rem;
        }
        @media (max-width: 720px) {
          .sliders-grid {
            grid-template-columns: 1fr;
          }
        }
        .slider-row {
          display: block;
          margin-bottom: 0;
        }
        .slider-label {
          display: block;
          font-size: 0.85rem;
          margin-bottom: 0.35rem;
        }
        .slider-label strong {
          color: var(--accent);
          font-weight: 600;
        }
        input[type="range"] {
          width: 100%;
          accent-color: var(--accent);
          min-height: 44px;
          padding-block: 0.5rem;
        }
        .live {
          margin-top: 0.5rem;
          padding: 0.65rem 0.75rem;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 8px;
          display: flex;
          align-items: baseline;
          gap: 0.45rem;
          flex-wrap: wrap;
        }
        .live strong {
          font-family: var(--mono);
          font-size: 1.1rem;
          color: var(--ok);
        }
        .muted {
          color: var(--muted);
          font-size: 0.85rem;
        }
      `}</style>
    </div>
  );
}
